"use client";

import React, { useState } from "react";
import Link from "next/link";
import CommonHero from "@/components/ui/CommonHero";

import {
    FiLayout,
    FiCode,
    FiImage,
    FiTrendingUp,
    FiBarChart2,
    FiFileText,
    FiHeart,
    FiHome,
    FiShoppingBag,
    FiCoffee,
    FiBookOpen,
    FiMessageSquare,
    FiMic,
} from "react-icons/fi";

/* =========================
   TYPES
========================= */
type IconType = React.ComponentType<{ size?: number }>;

type ServiceItem = {
    href: string;
    label: string;
    icon: keyof typeof iconMap;
};

type ServiceSection = {
    title: string;
    icon: keyof typeof iconMap;
    overviewHref: string;
    items: ServiceItem[];
};

/* =========================
   ICON MAP (SINGLE SOURCE)
========================= */
const iconMap: Record<string, IconType> = {
    layout: FiLayout,
    code: FiCode,
    image: FiImage,
    trending: FiTrendingUp,
    bar: FiBarChart2,
    file: FiFileText,
    heart: FiHeart,
    home: FiHome,
    bag: FiShoppingBag,
    coffee: FiCoffee,
    book: FiBookOpen,
    message: FiMessageSquare,
    mic: FiMic,
    ai: FiTrendingUp,
    staff: FiHome,
    growth: FiTrendingUp,
};

/* =========================
   CORE PAGES
========================= */
const corePages = [
    {
        href: "/about-us",
        label: "About Us",
        desc: "Who we are & what we stand for",
    },
    {
        href: "/brand-development-lifecycle",
        label: "BDLC",
        desc: "Our complete brand development framework",
    },
    {
        href: "/a-to-z-of-branding",
        label: "A to Z of Branding",
        desc: "Full guide to modern branding systems",
    },
];

/* =========================
   NAV DATA
========================= */
const navItems = [
    {
        href: "/services",
        label: "Services",
        dropdownSections: [
            {
                title: "Design/Development-as-a-Service",
                icon: "layout",
                overviewHref: "/design-development-as-a-service",
                items: [
                    { href: "/ui-ux-design", label: "UI/UX Design", icon: "layout" },
                    { href: "/website-development", label: "Website Development", icon: "code" },
                    { href: "/graphic-design", label: "Graphic Design", icon: "image" },
                ],
            },
            {
                title: "Growth-as-a-Service",
                icon: "growth",
                overviewHref: "/growth-as-a-service",
                items: [
                    { href: "/organic-visibility", label: "Organic Visibility", icon: "trending" },
                    { href: "/performance-marketing", label: "Performance Marketing", icon: "bar" },
                    { href: "/content-marketing", label: "Content Marketing", icon: "file" },
                ],
            },
            {
                title: "AI-as-a-Service",
                icon: "ai",
                overviewHref: "/ai-as-a-service",
                items: [],
            },
            {
                title: "Staff-as-a-Service",
                icon: "staff",
                overviewHref: "/staff-as-a-service",
                items: [],
            },
        ] as ServiceSection[],
    },
    {
        href: "/industries",
        label: "Industries",
        dropdownItems: [
            { href: "/industries/healthcare", label: "Healthcare", icon: "heart" },
            { href: "/industries/real-estate", label: "Real Estate", icon: "home" },
            { href: "/industries/e-commerce", label: "E-Commerce", icon: "bag" },
            { href: "/industries/hospitality", label: "Hospitality", icon: "coffee" },
            { href: "/industries/ed-tech", label: "EdTech", icon: "book" },
        ],
    },
    {
        href: "/success-stories",
        label: "Success Stories",
        dropdownItems: [
            { href: "/case-study", label: "Case Studies", icon: "book" },
            { href: "/testimonial", label: "Testimonials", icon: "message" },
        ],
    },
    {
        href: "/resources",
        label: "Resources",
        dropdownItems: [
            { href: "/blogs", label: "Blogs", icon: "book" },
            { href: "/news-pr", label: "News & PR", icon: "mic" },
            { href: "/thought-leadership", label: "Thought Leadership", icon: "file" },
        ],
    },
];

/* =========================
   PAGE
========================= */
export default function SitemapPage() {
    const [open, setOpen] = useState<number | null>(null);

    const toggle = (i: number) => {
        setOpen(open === i ? null : i);
    };

    /* =========================
       SERVICE BLOCK (TYPED)
    ========================= */
    const ServiceBlock = ({
        sec,
    }: {
        sec: ServiceSection;
    }) => {
        const Icon = iconMap[sec.icon];

        return (
            <div>
                <Link
                    href={sec.overviewHref}
                    className="flex items-center gap-2 font-medium text-[#272d57] mb-3 hover:underline underline-offset-4"
                >
                    <span className="text-sm border border-gray-200 rounded p-1">
                        {Icon && <Icon size={14} />}
                    </span>
                    {sec.title}
                </Link>

                {sec.items.length > 0 && (
                    <div className="pl-5 space-y-2">
                        {sec.items.map((it, j) => {
                            const SubIcon = iconMap[it.icon];

                            return (
                                <Link
                                    key={j}
                                    href={it.href}
                                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#272d57] transition"
                                >
                                    {SubIcon && (
                                        <span className="text-xs border border-gray-200 rounded p-1">
                                            <SubIcon size={12} />
                                        </span>
                                    )}
                                    <span className="hover:underline underline-offset-4">
                                        {it.label}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    return (
        <section className="relative bg-white overflow-hidden">
            <CommonHero
                badgeText="MMB"
                title="Sitemap"
                align="left"
                bannerTitle="Explore Everything in One Place"
                bannerDescription="Navigate all pages of the website including services, industries, resources, and core brand foundations."
                buttonLink="/contact-us"
                bannerButtonLabel="Contact Us"
            />

            {/* ================= CORE HERO SECTION ================= */}
            <div className="custom-container mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="mb-5">
                    <h2 className="text-2xl font-semibold text-[#272d57] mb-6">
                        Core Brand Foundation
                    </h2>

                    <div className="grid md:grid-cols-3 gap-6">
                        {corePages.map((item, i) => (
                            <Link
                                key={i}
                                href={item.href}
                                className="group relative overflow-hidden rounded-none border border-gray-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                            >
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.08),transparent_60%)]" />

                                <div className="relative">
                                    <h3 className="text-lg font-semibold text-[#272d57] ">
                                        {item.label}
                                    </h3>

                                    <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                                        {item.desc}
                                    </p>

                                    <div className="mt-4 flex items-center text-sm font-medium text-[#272d57]">
                                        Explore
                                        <span className="ml-2 transition group-hover:translate-x-1">
                                            →
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>

                {/* ================= DESKTOP GRID ================= */}
                <div className="hidden md:block space-y-5">

                    {/* ================= SERVICES (CUSTOM 3 COL) ================= */}
                    {navItems
                        .filter((item) => item.label === "Services")
                        .map((item, idx) => {
                            const sections = item.dropdownSections || [];

                            return (
                                <div
                                    key={idx}
                                    className="group relative overflow-hidden border border-gray-200 bg-white"
                                >
                                    <div className="relative p-6">
                                        <h2 className="text-xl font-semibold text-[#272d57] mb-6">
                                            {item.label}
                                        </h2>

                                        {/* 3 COL LAYOUT */}
                                        <div className="grid md:grid-cols-3 gap-8">

                                            {/* COL 1 */}
                                            {sections[0] && (
                                                <ServiceBlock sec={sections[0]} />
                                            )}

                                            {/* COL 2 */}
                                            {sections[1] && (
                                                <ServiceBlock sec={sections[1]} />
                                            )}

                                            {/* COL 3 (STACKED) */}
                                            <div className="space-y-8">
                                                {sections[2] && (
                                                    <ServiceBlock sec={sections[2]} />
                                                )}
                                                {sections[3] && (
                                                    <ServiceBlock sec={sections[3]} />
                                                )}
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                    {/* ================= INDUSTRIES (2 COL GRID) ================= */}
                    <div className="grid md:grid-cols-2 gap-5">

                        {navItems
                            .filter((item) => item.label === "Industries")
                            .map((item, idx) => (
                                <div
                                    key={idx}
                                    className="group relative overflow-hidden border border-gray-200 bg-white"
                                >
                                    <div className="relative p-6">
                                        <h2 className="text-xl font-semibold text-[#272d57] mb-6">
                                            {item.label}
                                        </h2>

                                        <div className="grid grid-cols-2 gap-4">
                                            {item.dropdownItems?.map((d, i) => {
                                                const Icon = iconMap[d.icon];

                                                return (
                                                    <Link
                                                        key={i}
                                                        href={d.href}
                                                        className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#272d57] transition"
                                                    >
                                                        <span className="text-sm border border-gray-200 rounded p-1">
                                                            {Icon && <Icon size={14} />}
                                                        </span>
                                                        <span className="hover:underline underline-offset-4">
                                                            {d.label}
                                                        </span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            ))}

                        {/* ================= SUCCESS + RESOURCES ================= */}
                        <div className="grid md:grid-cols-2 gap-6">
                            {navItems
                                .filter(
                                    (item) =>
                                        item.label === "Success Stories" || item.label === "Resources"
                                )
                                .map((item, idx) => (
                                    <div
                                        key={idx}
                                        className="group relative overflow-hidden border border-gray-200 bg-white"
                                    >
                                        <div className="relative p-6">
                                            <h2 className="text-xl font-semibold text-[#272d57] mb-5">
                                                {item.label}
                                            </h2>

                                            <div className="space-y-4">
                                                {item.dropdownItems?.map((d, i) => {
                                                    const Icon = iconMap[d.icon];

                                                    return (
                                                        <Link
                                                            key={i}
                                                            href={d.href}
                                                            className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#272d57] transition hover:translate-x-1"
                                                        >
                                                            <span className="text-sm border border-gray-200 rounded p-1">
                                                                {Icon && <Icon size={14} />}
                                                            </span>
                                                            <span className="hover:underline underline-offset-4">
                                                                {d.label}
                                                            </span>
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>

                </div>

                {/* ================= MOBILE ================= */}
                <div className="md:hidden space-y-4 mt-10">
                    {navItems.map((item, idx) => (
                        <div key={idx} className="border border-gray-200 overflow-hidden">
                            <button
                                onClick={() => toggle(idx)}
                                className="w-full flex justify-between items-center p-4 font-medium"
                            >
                                {item.label}
                                <span>{open === idx ? "−" : "+"}</span>
                            </button>

                            {open === idx && (
                                <div className="p-4 pt-0 space-y-2">
                                    {item.dropdownItems?.map((d, i) => {
                                        const Icon = iconMap[d.icon];
                                        return (
                                            <Link
                                                key={i}
                                                href={d.href}
                                                className="flex items-center gap-2 text-sm py-1"
                                            >
                                                {Icon && <Icon size={14} />}
                                                {d.label}
                                            </Link>
                                        );
                                    })}

                                    {item.dropdownSections?.map((sec, i) => (
                                        <div key={i}>
                                            <p className="font-medium text-sm mt-2">
                                                {sec.title}
                                            </p>

                                            <div className="pl-3 space-y-1">
                                                {sec.items.map((it, j) => (
                                                    <Link
                                                        key={j}
                                                        href={it.href}
                                                        className="block text-sm text-gray-600"
                                                    >
                                                        {it.label}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    ))}

                                    {item.href && !item.dropdownItems && (
                                        <Link href={item.href} className="text-sm">
                                            Visit page →
                                        </Link>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}